/**
 * W3 动作选用弹窗:平台/租户动作库 → 挑一条给当前镜(references[role=motion])。
 * 列表按需拉取(打开才请求);分类 chip 过滤;hover 播视频预览。
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Badge } from "@/shared/ui/badge";
import { cn } from "@/shared/lib/utils";
import {
  MOTION_CATEGORY_LABEL,
  videoAssetService,
  type VideoMotionAssetDto,
} from "../../api/videoAssetService";
import { errorText } from "@/shared/lib/apiErrors";

export function MotionPickerDialog({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (asset: VideoMotionAssetDto) => void;
}) {
  const [assets, setAssets] = useState<VideoMotionAssetDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!open || assets !== null) return;
    videoAssetService
      .listMotions()
      .then(setAssets)
      .catch((e: unknown) => setError(errorText(e)));
  }, [open, assets]);

  const categories = useMemo(() => [...new Set((assets ?? []).map((a) => a.category))], [assets]);
  const shown = (assets ?? []).filter((a) => !category || a.category === category);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-baseline justify-between gap-2 pr-6">
            <span>选一个动作(本镜主体照它演)</span>
            {/* 资产库此前无任何入口(只能手敲 URL)—— 挑动作的人就是要管库的人,入口放这。 */}
            <Link
              to="/ai-video/assets"
              className="text-xs font-normal text-muted-foreground underline-offset-2 hover:underline"
            >
              管理动作库 →
            </Link>
          </DialogTitle>
        </DialogHeader>
        {error && <p className="text-sm text-destructive">动作库加载失败:{error}</p>}
        {!error && assets === null && <p className="text-sm text-muted-foreground">加载中…</p>}
        {assets !== null && assets.length === 0 && (
          <p className="text-sm text-muted-foreground">动作库还没有素材</p>
        )}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setCategory(null)}
              className={cn(
                "cursor-pointer rounded-full border px-2 py-0.5 text-xs",
                category === null ? "border-primary bg-primary/10 text-primary" : "border-border/60"
              )}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={cn(
                  "cursor-pointer rounded-full border px-2 py-0.5 text-xs",
                  category === c ? "border-primary bg-primary/10 text-primary" : "border-border/60"
                )}
              >
                {MOTION_CATEGORY_LABEL[c] ?? c}
              </button>
            ))}
          </div>
        )}
        <div className="grid max-h-[50vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3">
          {shown.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                onPick(a);
                onOpenChange(false);
              }}
              className="group cursor-pointer rounded-lg border p-2 text-left transition-colors hover:border-primary"
            >
              {a.url ? (
                <video
                  src={a.url}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="aspect-video w-full rounded bg-muted object-cover"
                  onMouseEnter={(e) => void e.currentTarget.play().catch(() => undefined)}
                  onMouseLeave={(e) => e.currentTarget.pause()}
                />
              ) : (
                <div className="aspect-video w-full rounded bg-muted" />
              )}
              <div className="mt-1.5 flex items-center gap-1">
                <span className="truncate text-xs font-medium">{a.name}</span>
                <span className="ml-auto shrink-0 text-2xs text-muted-foreground">
                  {a.durationSec != null ? `${a.durationSec}s` : ""}
                </span>
              </div>
              <Badge variant="secondary" className="mt-0.5 text-2xs">
                {MOTION_CATEGORY_LABEL[a.category] ?? a.category}
              </Badge>
            </button>
          ))}
        </div>
        <p className="text-2xs leading-snug text-muted-foreground">
          只取动作:素材里的场景与人物外观不会进成片(渲染层有护栏)。
        </p>
      </DialogContent>
    </Dialog>
  );
}
